import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { FileText, Save, Eye, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card, CardContent } from "../components/ui/card";
import { api } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { toast } from "sonner";

const SLUGS = [
  { slug: "histoire", icon: "📖", label: "Notre histoire", public_path: "/boutique/histoire" },
  { slug: "mentions", icon: "⚖️", label: "Mentions légales", public_path: "/boutique/mentions" },
  { slug: "cgv", icon: "📜", label: "CGV", public_path: "/boutique/cgv" },
  { slug: "confidentialite", icon: "🔒", label: "Confidentialité", public_path: "/boutique/confidentialite" },
];

// Mini Markdown cheat-sheet (sidebar)
const MD_HELP = [
  { md: "## Titre", desc: "Titre de section" },
  { md: "### Sous-titre", desc: "Sous-titre" },
  { md: "**gras**", desc: "Mise en valeur" },
  { md: "*italique*", desc: "Italique discret" },
  { md: "- item", desc: "Liste à puces" },
  { md: "[lien](https://…)", desc: "Lien" },
  { md: "> citation", desc: "Citation / accroche" },
  { md: "---", desc: "Séparateur horizontal" },
];

export default function LegalPagesAdmin() {
  const [pages, setPages] = useState([]);
  const [activeSlug, setActiveSlug] = useState("histoire");
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/legal-pages");
    setPages(data);
    // Init editing state from data
    const e = {};
    for (const p of data) {
      e[p.slug] = { title: p.title || "", eyebrow: p.eyebrow || "", content_md: p.content_md || "" };
    }
    setEditing(e);
  };
  useEffect(() => { load(); }, []);

  const active = pages.find((p) => p.slug === activeSlug);
  const draft = editing[activeSlug] || { title: "", eyebrow: "", content_md: "" };
  const setDraft = (patch) => setEditing({ ...editing, [activeSlug]: { ...draft, ...patch } });

  const isDirty = active && (
    draft.title !== (active.title || "") ||
    draft.eyebrow !== (active.eyebrow || "") ||
    draft.content_md !== (active.content_md || "")
  );

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/legal-pages/${activeSlug}`, draft);
      toast.success("Page enregistrée. Les visiteurs verront la mise à jour immédiatement.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="legal-pages-admin">
      <div>
        <h1 className="text-2xl font-heading text-[#1A1C1A] flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#1F4E3D]" /> Pages éditoriales
        </h1>
        <p className="text-sm text-[#575A57]">
          Éditez le contenu public des pages CGV, Mentions, Histoire et Confidentialité.
          <br />
          <em className="text-[#A3A6A3]">Formatage Markdown : voir l'aide à droite.</em>
        </p>
      </div>

      <Tabs value={activeSlug} onValueChange={setActiveSlug}>
        <TabsList className="bg-[#F4F4F0]">
          {SLUGS.map((s) => (
            <TabsTrigger key={s.slug} value={s.slug} data-testid={`tab-${s.slug}`}>
              <span className="mr-2">{s.icon}</span>{s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {SLUGS.map((s) => (
          <TabsContent key={s.slug} value={s.slug}>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
              <Card className="border-[#E5E5E0]">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <h2 className="font-heading text-xl text-[#1A1C1A]">{s.icon} {s.label}</h2>
                      <a href={s.public_path} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1F4E3D] hover:underline flex items-center gap-1" data-testid={`open-public-${s.slug}`}>
                        <ExternalLink className="w-3 h-3" /> Voir la page publique
                      </a>
                    </div>
                    {active?.updated_at && (
                      <span className="text-[10px] uppercase tracking-wider text-[#A3A6A3]" data-testid={`updated-${s.slug}`}>
                        Mise à jour : {fmtDateTime(active.updated_at)}
                      </span>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-1.5 block">Surtitre (eyebrow)</Label>
                    <Input
                      value={draft.eyebrow}
                      onChange={(e) => setDraft({ eyebrow: e.target.value })}
                      placeholder="Ex: « CGV », « Impressum »…"
                      data-testid={`field-eyebrow-${s.slug}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-1.5 block">Titre principal</Label>
                    <Input
                      value={draft.title}
                      onChange={(e) => setDraft({ title: e.target.value })}
                      placeholder={s.label}
                      data-testid={`field-title-${s.slug}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-1.5 block">Contenu (Markdown)</Label>
                    <Textarea
                      rows={22}
                      value={draft.content_md}
                      onChange={(e) => setDraft({ content_md: e.target.value })}
                      className="font-mono text-xs leading-relaxed"
                      placeholder="## Titre&#10;Paragraphe…"
                      data-testid={`field-content-${s.slug}`}
                    />
                    <div className="text-[10px] text-[#A3A6A3] mt-1 text-right font-mono">
                      {(draft.content_md || "").length} caractères
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-[#F0F0EB]">
                    <span className="text-xs text-[#575A57]">
                      {isDirty ? "🟡 Modifications non enregistrées" : "✓ À jour"}
                    </span>
                    <Button
                      onClick={save}
                      disabled={!isDirty || saving}
                      className="bg-[#1F4E3D] hover:bg-[#173B2E]"
                      data-testid={`save-${s.slug}`}
                    >
                      {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement…</> : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Side panel: cheat-sheet + preview link */}
              <div className="space-y-3">
                <Card className="border-[#E5E5E0]">
                  <CardContent className="p-4 text-xs">
                    <h3 className="font-semibold text-[#1A1C1A] mb-2 flex items-center gap-1.5">
                      <Eye className="w-3.5 h-3.5" /> Aide Markdown
                    </h3>
                    <ul className="space-y-1.5">
                      {MD_HELP.map((h) => (
                        <li key={h.md} className="flex justify-between gap-3">
                          <code className="text-[10px] text-[#1F4E3D] font-mono">{h.md}</code>
                          <span className="text-[#575A57] text-right">{h.desc}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-[#E5E5E0]">
                  <CardContent className="p-4 space-y-2">
                    <h3 className="text-xs font-semibold text-[#1A1C1A]">Aperçu rendu</h3>
                    <div className="p-3 bg-[#0A0A0A] text-[#F2ECE4] rounded text-xs max-h-[420px] overflow-y-auto" data-testid={`preview-${s.slug}`}>
                      <div className="prose-shop-mini">
                        <ReactMarkdown
                          components={{
                            h1: (p) => <h3 className="text-base font-bold mb-2" style={{ color: "#F2ECE4" }} {...p} />,
                            h2: (p) => <h3 className="text-sm font-bold mt-3 mb-1.5" style={{ color: "#F2ECE4" }} {...p} />,
                            h3: (p) => <h4 className="text-xs font-semibold mt-2 mb-1" style={{ color: "#F2ECE4" }} {...p} />,
                            p: (p) => <p className="my-1.5 leading-relaxed" style={{ color: "#A6A29C" }} {...p} />,
                            strong: (p) => <strong style={{ color: "#F2ECE4" }} {...p} />,
                            em: (p) => <em style={{ color: "#737373", fontStyle: "italic" }} {...p} />,
                            ul: (p) => <ul className="my-1.5 ml-4 list-disc" {...p} />,
                            li: (p) => <li className="leading-relaxed" style={{ color: "#A6A29C" }} {...p} />,
                            a: (p) => <a style={{ color: "#C1A063" }} {...p} />,
                            blockquote: (p) => <blockquote className="border-l-2 pl-2 my-2 italic" style={{ borderColor: "#C1A063", color: "#F2ECE4" }} {...p} />,
                            hr: () => <hr className="my-3 border-t" style={{ borderColor: "rgba(242,236,228,0.1)" }} />,
                            code: (p) => <code className="px-1 rounded text-[10px]" style={{ background: "rgba(193,160,99,0.15)", color: "#C1A063" }} {...p} />,
                          }}
                        >
                          {draft.content_md || "*(vide)*"}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
